import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, FileSpreadsheet, FileText, Loader2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrencySettings } from "@/hooks/useCurrencySettings";
import type { AssistantDocSpec } from "@/hooks/useAssistantStream";
import { aBase64, buscarInforme, ficheroDelInforme, informesDisponibles, leerRespuestaEnvio, leerTarjetaEmail, type ResultadoEnvio } from "@/lib/reportEmail/enviar";
import { ASUNTO_MAX, MENSAJE_MAX, parseDestinatarios } from "@/lib/reportEmail/reportEmailRules";
import { cargarMarcaInforme } from "@/lib/marca/cabeceraInforme";

type Estado = "idle" | "sending" | "sent" | "discarded" | "error";

/**
 * «Enviar por email» (27-09-2026, A21): la tarjeta que dibuja `propose_email`.
 *
 * El asistente PROPONE y la persona envía: aquí se ven (y se pueden corregir)
 * los destinatarios, el asunto y el mensaje, y QUÉ fichero va —el informe de
 * esta conversación o la versión congelada del cuaderno—. Solo al pulsar
 * «Enviar» se llama a la edge `send-report-email` con la sesión de la persona;
 * la edge lo vuelve a comprobar todo. Después: «Email enviado a …».
 *
 * La tarjeta se guarda con la conversación: al reabrirla, pregunta a
 * `report_email_sends` por su llave (`send_id`) y, si ya salió, lo dice en vez
 * de ofrecer «Enviar» otra vez (la edge tampoco mandaría dos veces).
 */
export function AssistantEmailCard({ item, docs }: { item: Record<string, unknown>; docs: AssistantDocSpec[] }) {
  const { t, i18n } = useTranslation();
  const { company } = useAuth();
  const { formatCurrency } = useCurrencySettings();
  const tarjeta = useMemo(() => leerTarjetaEmail(item), [item]);

  const [para, setPara] = useState(() => (tarjeta?.to ?? []).join(", "));
  const [asunto, setAsunto] = useState(tarjeta?.subject ?? "");
  const [mensaje, setMensaje] = useState(tarjeta?.message ?? "");
  const [estado, setEstado] = useState<Estado>("idle");
  const [enviadoA, setEnviadoA] = useState<string[]>([]);
  const [simulado, setSimulado] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const adjunto = tarjeta?.attachment;
  /* Qué informe va: el que dijo (título o tipo) o el último; con varios en la
     conversación, la persona lo cambia en el selector (28-09-2026). */
  const disponibles = useMemo(() => informesDisponibles(docs), [docs]);
  const propuesto = adjunto?.type === "report" ? buscarInforme(docs, adjunto.report_id, adjunto.report_title) : null;
  const [elegido, setElegido] = useState<number | null>(null);
  const informe = adjunto?.type === "report" ? (elegido != null ? disponibles[elegido] ?? propuesto : propuesto) : null;
  const sinInforme = adjunto?.type === "report" && !informe;

  // ¿Ya salió? (la tarjeta vuelve al reabrir la conversación)
  useEffect(() => {
    if (!tarjeta) return;
    let vivo = true;
    void (async () => {
      try {
        const { data } = await supabase
          .from("report_email_sends")
          .select("status, recipients")
          .eq("request_id", tarjeta.send_id)
          .maybeSingle();
        if (!vivo || !data) return;
        if (data.status === "sent" || data.status === "simulated") {
          setEnviadoA(data.recipients ?? []);
          setSimulado(data.status === "simulated");
          setEstado("sent");
        }
      } catch {
        /* sin poder leerlo, la tarjeta ofrece Enviar: la edge no manda dos veces */
      }
    })();
    return () => {
      vivo = false;
    };
  }, [tarjeta]);

  if (!tarjeta || !adjunto) return null;

  const formatoTxt = adjunto.format === "xlsx" ? "Excel" : "PDF";
  const titulo =
    adjunto.type === "cuaderno"
      ? t("ipmAssistant.emailCard.cuadernoTitle", "Cuaderno de explotación {{year}} · versión {{version}}", { year: adjunto.year, version: adjunto.version })
      : informe?.title ?? t("ipmAssistant.emailCard.reportFallback", "Informe");
  const detalle =
    adjunto.type === "cuaderno"
      ? t("ipmAssistant.emailCard.cuadernoFile", "Versión congelada · {{format}}", { format: formatoTxt })
      : t("ipmAssistant.emailCard.reportFile", "Informe del chat · {{format}}", { format: formatoTxt });
  const Icono = adjunto.format === "xlsx" ? FileSpreadsheet : FileText;

  const dest = parseDestinatarios(para);
  const destinosMal = dest.invalidos.length > 0 || dest.validos.length === 0 || dest.demasiados;

  const enviar = async () => {
    if (estado === "sending" || destinosMal || sinInforme) return;
    setEstado("sending");
    setErrorCode(null);
    try {
      const base = {
        request_id: tarjeta.send_id,
        recipients: dest.validos,
        subject: asunto.trim(),
        message: mensaje.trim(),
        language: i18n.language,
      };
      let body: Record<string, unknown>;
      if (adjunto.type === "cuaderno") {
        body = { ...base, source: "cuaderno", cuaderno_export_id: adjunto.export_id };
      } else {
        const f = await ficheroDelInforme(informe!, adjunto.format, {
          companyName: company?.name ?? "",
          // Con la cabecera que pidió el chat (F4): el adjunto sale igual que la descarga.
          marca: await cargarMarcaInforme(company?.id, informe!.cabecera),
          language: i18n.language,
          t: (k) => t(k),
          formatCurrency,
        });
        body = {
          ...base,
          source: "report",
          report: { title: informe!.title, format: adjunto.format, file_name: f.fileName, file_base64: aBase64(f.bytes) },
        };
      }
      const { data, error } = await supabase.functions.invoke("send-report-email", { body });
      const r = await leerRespuestaEnvio(data, error);
      if (r.ok === false) {
        setErrorCode((r as Extract<ResultadoEnvio, { ok: false }>).code);
        setEstado("error");
      } else {
        setEnviadoA(r.recipients);
        setSimulado(r.status === "simulated");
        setEstado("sent");
      }
    } catch (e) {
      console.error("[AssistantEmailCard] envío fallido:", e);
      setErrorCode("EMAIL_ERROR");
      setEstado("error");
    }
  };

  const campo = "w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground outline-none focus:border-[#2E84DE] disabled:opacity-60";
  const bloqueado = estado === "sending" || estado === "sent" || estado === "discarded";

  return (
    <div data-testid="assistant-email-card" className="mt-2 w-full rounded-2xl border border-[#b6dded] bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2E84DE] text-white">
          <Mail className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("ipmAssistant.emailCard.title", "Enviar por email")}
          </span>
          <span data-testid="assistant-email-file" className="flex items-center gap-1.5 text-sm font-bold text-foreground">
            <Icono className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 break-words">{titulo}</span>
          </span>
          <span className="block text-xs text-muted-foreground">{detalle}</span>
        </span>
      </div>

      {adjunto.type === "report" && disponibles.length > 1 && estado !== "sent" && estado !== "discarded" && (
        <label className="mt-2 block">
          <span className="mb-0.5 block text-[11px] font-semibold text-muted-foreground">{t("ipmAssistant.emailCard.reportFallback", "Informe")}</span>
          <select
            data-testid="assistant-email-report"
            value={Math.max(0, disponibles.indexOf(informe as AssistantDocSpec))}
            onChange={(e) => setElegido(Number(e.target.value))}
            disabled={estado === "sending"}
            className="w-full rounded-xl border bg-muted/40 px-3 py-2 text-sm"
          >
            {disponibles.map((d, i) => (
              <option key={i} value={i}>
                {d.title}
              </option>
            ))}
          </select>
        </label>
      )}

      {sinInforme && (
        <p data-testid="assistant-email-no-report" className="mt-2 text-xs font-medium text-destructive">
          {t("ipmAssistant.emailCard.noReport", "No hay ningún informe en esta conversación: pídemelo primero.")}
        </p>
      )}

      {estado !== "sent" && estado !== "discarded" && (
        <div className="mt-2.5 space-y-2">
          <label className="block">
            <span className="mb-0.5 block text-[11px] font-semibold text-muted-foreground">{t("ipmAssistant.emailCard.to", "Para")}</span>
            <input
              data-testid="assistant-email-to"
              type="email"
              multiple
              inputMode="email"
              autoComplete="off"
              value={para}
              onChange={(e) => setPara(e.target.value)}
              disabled={bloqueado}
              className={campo}
            />
          </label>
          {para.trim() !== "" && dest.invalidos.length > 0 && (
            <p data-testid="assistant-email-invalid" className="text-[11px] text-destructive">
              {t("ipmAssistant.emailCard.invalid", "Revisa estas direcciones: {{list}}", { list: dest.invalidos.join(", ") })}
            </p>
          )}
          {dest.demasiados && (
            <p className="text-[11px] text-destructive">{t("ipmAssistant.emailCard.error.EMAIL_DEMASIADOS_DESTINOS", "Como mucho 5 destinatarios por correo.")}</p>
          )}
          <label className="block">
            <span className="mb-0.5 block text-[11px] font-semibold text-muted-foreground">{t("ipmAssistant.emailCard.subject", "Asunto")}</span>
            <input
              data-testid="assistant-email-subject"
              value={asunto}
              maxLength={ASUNTO_MAX}
              onChange={(e) => setAsunto(e.target.value)}
              placeholder={t("ipmAssistant.emailCard.subjectPlaceholder", "{{company}} te envía: {{title}}", { company: company?.name ?? "", title: titulo })}
              disabled={bloqueado}
              className={campo}
            />
          </label>
          <label className="block">
            <span className="mb-0.5 block text-[11px] font-semibold text-muted-foreground">{t("ipmAssistant.emailCard.message", "Mensaje")}</span>
            <textarea
              data-testid="assistant-email-message"
              value={mensaje}
              maxLength={MENSAJE_MAX}
              rows={3}
              onChange={(e) => setMensaje(e.target.value)}
              placeholder={t("ipmAssistant.emailCard.messagePlaceholder", "Opcional")}
              disabled={bloqueado}
              className={`${campo} resize-none`}
            />
          </label>
          <p className="text-[11px] text-muted-foreground">
            {t("ipmAssistant.emailCard.linkNote", "Lleva un botón «Descargar» que caduca a los 7 días y, si pesa poco, también el fichero adjunto.")}
          </p>
        </div>
      )}

      {estado === "sent" && (
        <p data-testid="assistant-email-sent" className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-emerald-700">
          <CheckCircle2 className="mt-px h-4 w-4 shrink-0" />
          <span>
            {simulado
              ? t("ipmAssistant.emailCard.simulated", "Simulado (cuenta de pruebas): no ha salido ningún correo a {{list}}.", { list: enviadoA.join(", ") })
              : t("ipmAssistant.emailCard.sent", "Email enviado a {{list}}", { list: enviadoA.join(", ") })}
          </span>
        </p>
      )}
      {estado === "discarded" && (
        <p className="mt-2 text-xs text-muted-foreground">{t("ipmAssistant.emailCard.discarded", "Descartado: no se ha enviado nada.")}</p>
      )}
      {estado === "error" && errorCode && (
        <p data-testid="assistant-email-error" className="mt-2 text-xs font-medium text-destructive">
          {t(`ipmAssistant.emailCard.error.${errorCode}`, t("ipmAssistant.emailCard.error.EMAIL_ERROR", "No se ha podido enviar. Inténtalo otra vez."))}
        </p>
      )}

      {(estado === "idle" || estado === "sending" || estado === "error") && (
        <div className="mt-2.5 flex gap-2">
          <button
            type="button"
            onClick={enviar}
            disabled={estado === "sending" || destinosMal || sinInforme}
            data-testid="assistant-email-send"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#2E84DE] px-3 text-[13px] font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {estado === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("ipmAssistant.emailCard.send", "Enviar")}
          </button>
          <button
            type="button"
            onClick={() => setEstado("discarded")}
            disabled={estado === "sending"}
            data-testid="assistant-email-discard"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-xl border border-border px-3 text-[13px] font-semibold text-foreground transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {t("ipmAssistant.emailCard.discard", "Descartar")}
          </button>
        </div>
      )}
    </div>
  );
}
